/*!
  Pioneer WFRIA RadioButton v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["RadioButton"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("RadioButton") < 0) {
    window.__PIONEER_GLOBAL__.components.push("RadioButton");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["name", "className", "disabled", "display", "displayErrors", "fieldControlClassName", "id", "label", "labelHidden", "labelLocation", "onBlur", "onChange", "onValueChange", "required", "spacing"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useUniqueId } from "../common/hooks";
import { isIOS, registerField } from "../common/utilities";
import { FieldsetContext } from "../Fieldset/FieldsetContext";
import { FormContext } from "../Form/FormContext";
import Box from "../private/Box/Box";
import { RADIOBUTTON_LABEL_CLASS, RADIOBUTTON_LABEL_ERROR_CLASS, RADIOBUTTON_LABEL_TEXT_CLASS } from "../themes/common/classes";
import { visuallyHidden } from "../themes/common/styles";
import extractTheme from "./extractTheme";
/**
 * RadioButton is a React component that adds functionality to an `<input>` of type `radio`.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const RadioButton = /*#__PURE__*/React.forwardRef((_ref, userRef) => {
  let {
    name,
    className,
    disabled,
    display,
    displayErrors,
    fieldControlClassName,
    id,
    label,
    labelHidden,
    labelLocation,
    onBlur,
    onChange,
    onValueChange,
    required,
    spacing
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    checked
  } = otherProps;
  const {
    cssRoot,
    cssInput,
    cssLabel,
    cssLabelText
  } = extractTheme({
    theme,
    labelLocation,
    labelHidden,
    displayErrors
  });
  const radioButtonRef = useRef();
  const inputId = useUniqueId({
    id,
    prefix: "pnr-radio-button-"
  });
  const labelId = useUniqueId({
    prefix: "pnr-radio-button--label-"
  });
  const {
    disabled: disabledFromFieldsetContext
  } = useContext(FieldsetContext) || {};
  const [isDisabled, setIsDisabled] = useState(disabled || disabledFromFieldsetContext);
  const {
    register,
    resetDone,
    unregister,
    setValue,
    getValues
  } = useContext(FormContext) || {};
  /**
   * Handles the change event on the radio button.
   * Executes the passed onValueChange function with an object
   * containing the following signature: {e, value, name, id, type}.
   */

  const handleValueChange = e => {
    if (onValueChange) {
      const {
        name,
        id,
        value
      } = e.target;
      onValueChange({
        e,
        value,
        name,
        id,
        type: "radio"
      });
    }

    onChange && onChange(e);
  };
  /**
   * Registering our field with the "register" method from
   * react-hook-form.
   */


  const {
    inputRef,
    reactHookFormRest
  } = registerField({
    register,
    name,
    required,
    handleBlur: onBlur,
    handleChange: handleValueChange
  });
  useEffect(() => {
    return () => {
      /**
       * Component is being unmounted.
       * Un-registering field with the "unregister" method
       * from react-hook-form.
       */
      unregister && unregister(name);
    };
  }, [name, unregister]);
  /**
   * Keep disabled state current with disabled prop changes.
   */

  useEffect(() => {
    setIsDisabled(disabled || disabledFromFieldsetContext);
  }, [disabled, disabledFromFieldsetContext]);
  /**
   * Listen for changes to enclosing fieldset disabled state.
   */

  useEffect(() => {
    const enclosingFieldset = radioButtonRef.current.closest("fieldset");

    if (enclosingFieldset) {
      if (enclosingFieldset.disabled) {
        setIsDisabled(true);
      }

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === "disabled") {
            setIsDisabled(enclosingFieldset.disabled);
          }
        });
      });
      observer.observe(enclosingFieldset, {
        attributes: true
      });
      return () => observer.disconnect();
    }
  }, []);
  /**
   * Reset RadioButton on React Hook Form reset.
   */

  useEffect(() => {
    if (resetDone) {
      radioButtonRef.current.checked = false;
    }
  }, [resetDone]);
  useEffect(() => {
    /**
     * When RadioButton's checked status is updated from the state (not directly from RadioButton click)
     * then it's the RadioButton's responsibility to update its value in react-hook-form.
     * It does this by using react-hook-form's setValue method to update react-hook-form.
     *
     * When RadioButton's checked status is updated to false from the state
     * then we need to check if that RadioButton's value already exists in react-hook-form
     * and then we need to remove it from react-hook-form
     */
    const selectedValue = getValues && getValues(name);

    if (setValue) {
      if (checked) {
        setValue(name, radioButtonRef.current.value);
      } else if (selectedValue && selectedValue === radioButtonRef.current.value) {
        setValue(name, null);
      }
    }
  }, [checked]); // eslint-disable-line react-hooks/exhaustive-deps

  const radioButtonContainerClasses = clsx(className, "pnr-radio-button");
  const inputClassNames = clsx(fieldControlClassName, "pnr-radio-button--input");
  const labelClassNames = clsx({
    [RADIOBUTTON_LABEL_CLASS]: true,
    [RADIOBUTTON_LABEL_ERROR_CLASS]: displayErrors.length > 0
  });
  const disableVoiceOverFocus = isIOS && labelHidden;
  return _jsx(Box, {
    spacing: spacing,
    display: display,
    theme: theme,
    children: _jsxs("div", {
      className: radioButtonContainerClasses,
      css: cssRoot,
      children: [_jsx("input", _objectSpread(_objectSpread(_objectSpread({
        type: "radio",
        name: name,
        className: inputClassNames,
        css: cssInput
      }, otherProps), reactHookFormRest), {}, {
        ref: e => {
          /**
           * Normally, react-hook-form does that automatically.
           * In this case, we want to share the ref with the user, so
           * we need to run this ourselves.
           *
           * See "How to share ref usage?" in the FAQs.
           * https://react-hook-form.com/faqs
           */
          inputRef && inputRef(e);
          radioButtonRef.current = e;

          if (userRef) {
            userRef.current = e;
          }
        },
        id: inputId,
        disabled: isDisabled
      })), _jsx("label", {
        htmlFor: inputId,
        className: labelClassNames,
        css: cssLabel
        /**
         * Assistive technology needs to announce the label when it is visually hidden.
         * We are adding `aria-hidden` to the visually hidden label text below to avoid
         * unwanted focus for voiceover. Due to this, the announcement will not happen for voiceover.
         * To fix this, we are adding aria-labelledby so that voiceover makes
         * the label announcement after focusing on the radio input element.
         */
        ,
        "aria-labelledby": disableVoiceOverFocus ? labelId : null,
        children: _jsx("span", {
          /**
           * This attribute is necessary to avoid the unwanted focus on hidden text in voiceover.
           */
          "aria-hidden": disableVoiceOverFocus ? "true" : null,
          className: RADIOBUTTON_LABEL_TEXT_CLASS,
          css: cssLabelText,
          children: label
        })
      }), disableVoiceOverFocus && _jsx("span", {
        css: visuallyHidden,
        id: labelId,
        "aria-hidden": "true",
        children: label
      })]
    })
  });
});
RadioButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /** RadioButton label */
  label: PropTypes.node.isRequired,

  /** Unique name identifying this RadioButton within a form. */
  name: PropTypes.string.isRequired,

  /** Class name for the enclosing Box. */
  className: PropTypes.string,

  /** Disable or enable the look of a RadioButton. */
  disabled: PropTypes.bool,

  /** CSS display type for enclosing Box */
  display: PropTypes.string,

  /**
   * Based on the errors length the error based `SVG's` are applied.
   * If the list is zero then normal radio `SVG's` are shown
   * else error based `SVG's` are shown.
   */
  displayErrors: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** CSS class to apply to the HTML input field. */
  fieldControlClassName: PropTypes.string,

  /** The input id value. While optional, if an id is not added, one will be generated. */
  id: PropTypes.string,

  /** Visually hides the label but allows for a label to exist for assistive technology. */
  labelHidden: PropTypes.bool,

  /** Places the label either on left or right side of the RadioButton. */
  labelLocation: PropTypes.oneOf(["right", "left"]),

  /**
   * Callback to be executed on change of the RadioButton.
   * The onValueChange function differs from onChange in that it receives an object
   * with the following signature as a parameter. {value}
   */
  onValueChange: PropTypes.func,

  /** If set to `true`, RadioButton will be registered with Form as "required". */
  required: PropTypes.bool,

  /**
   * Custom spacing for the component. See the
   * "Customization -> Spacing" section.
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.shape()])
} : {};
RadioButton.defaultProps = {
  className: "",
  disabled: false,
  display: "block",
  displayErrors: [],
  fieldControlClassName: "",
  id: null,
  label: null,
  labelHidden: false,
  labelLocation: "right",
  onValueChange: null,
  required: false,
  spacing: {}
};
RadioButton.displayName = "RadioButton";
export default RadioButton;